import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, IconButton,
  CircularProgress, Box, makeStyles,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PrintIcon from '@material-ui/icons/Print'
import LockIcon from '@material-ui/icons/Lock'
import api from '../../api/client'
import { toEnglishDigits } from '../../assets/utils'

const useStyles = makeStyles((theme) => ({
  dialog: { direction: 'rtl' },
  title: {
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
    color: '#fff',
    padding: theme.spacing(2, 3),
  },
  closeBtn: { color: '#fff', position: 'absolute', left: 8, top: 8 },
  hint: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    fontSize: 13,
    lineHeight: 1.9,
  },
  errorBox: {
    background: '#ffebee',
    border: '1px solid #ef9a9a',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    color: '#c62828',
    fontSize: 13,
  },
  lockedBox: {
    background: '#eceff1',
    border: '1px solid #cfd8dc',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  lockedRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.6, 0),
    fontSize: 13,
  },
  lockedLabel: { color: '#607d8b', display: 'flex', alignItems: 'center', gap: 4 },
  lockedValue: { fontWeight: 'bold' },
  lockedNote: {
    fontSize: 11,
    color: '#78909c',
    marginTop: theme.spacing(0.5),
  },
  loading: { display: 'flex', justifyContent: 'center', padding: theme.spacing(4) },
}))

// فیلدهای قابل ویرایش (نام شرکت، موبایل، شناسه ملی و شماره اقتصادی اینجا نیستند —
// فقط از پنل تغییر می‌کنند یا اصلاً غیرقابل تغییرند)
const FIELDS = [
  { key: 'postal_code', label: 'کد پستی', sm: 6, ltr: true },
  { key: 'address', label: 'نشانی', sm: 12, multiline: true },
]

const NUMERIC_KEYS = ['national_code', 'economic_code', 'postal_code']

const emptyForm = {
  name: '', mobile: '',
  national_code: '', economic_code: '', postal_code: '', address: '',
}

/**
 * پاپ‌آپ مشخصات خریدار — قبل از صدور فاکتور باز می‌شود.
 * props:
 *   open, onClose
 *   onConfirm()  : بعد از ذخیره موفق، برای ادامه صدور فاکتور صدا زده می‌شود
 *   confirmLabel : متن دکمه تأیید
 */
const BillingProfileDialog = ({ open, onClose, onConfirm, confirmLabel = 'ذخیره و صدور فاکتور' }) => {
  const classes = useStyles()
  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await api.get('users/billing-profile/')
        setForm({ ...emptyForm, ...(res.data || {}) })
      } catch (err) {
        console.error('Error loading billing profile:', err)
        setError(err.response?.data?.detail || 'خطا در بارگذاری اطلاعات')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [open])

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = {}
      FIELDS.forEach(({ key }) => {
        let v = form[key] || ''
        if (NUMERIC_KEYS.includes(key)) v = toEnglishDigits(String(v))
        payload[key] = v
      })

      await api.put('users/billing-profile/', payload)
      if (onConfirm) onConfirm()
    } catch (err) {
      console.error('Error saving billing profile:', err)
      setError(err.response?.data?.detail || 'خطا در ذخیره اطلاعات')
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth className={classes.dialog}>
      <DialogTitle className={classes.title} disableTypography>
        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
          مشخصات خریدار
        </Typography>
        <IconButton className={classes.closeBtn} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 16 }}>
        {loading ? (
          <div className={classes.loading}><CircularProgress /></div>
        ) : (
          <>
            <Box className={classes.hint}>
              این اطلاعات روی فاکتور چاپ می‌شود و در پروفایل شما ذخیره می‌ماند.
              می‌توانید قبل از صدور فاکتور آن را ویرایش کنید.
            </Box>

            {error && <Box className={classes.errorBox}>{error}</Box>}

            {/* فیلدهای غیرقابل ویرایش */}
            <Box className={classes.lockedBox}>
              <div className={classes.lockedRow}>
                <span className={classes.lockedLabel}>
                  <LockIcon style={{ fontSize: 14 }} /> نام شرکت
                </span>
                <span className={classes.lockedValue}>{form.name || '—'}</span>
              </div>
              <div className={classes.lockedRow}>
                <span className={classes.lockedLabel}>
                  <LockIcon style={{ fontSize: 14 }} /> موبایل
                </span>
                <span className={classes.lockedValue} dir="ltr">{form.mobile || '—'}</span>
              </div>
              <div className={classes.lockedRow}>
                <span className={classes.lockedLabel}>
                  <LockIcon style={{ fontSize: 14 }} /> شناسه / شماره ملی
                </span>
                <span className={classes.lockedValue} dir="ltr">{form.national_code || '—'}</span>
              </div>
              <div className={classes.lockedRow}>
                <span className={classes.lockedLabel}>
                  <LockIcon style={{ fontSize: 14 }} /> شماره اقتصادی
                </span>
                <span className={classes.lockedValue} dir="ltr">{form.economic_code || '—'}</span>
              </div>
              <div className={classes.lockedNote}>
                این موارد غیرقابل تغییر است.
              </div>
            </Box>

            <Grid container spacing={2}>
              {FIELDS.map((f) => (
                <Grid item xs={12} sm={f.sm} key={f.key}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label={f.label}
                    value={form[f.key] || ''}
                    onChange={handleChange(f.key)}
                    multiline={f.multiline}
                    rows={f.multiline ? 2 : undefined}
                    dir={f.ltr ? 'ltr' : 'rtl'}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        {!loading && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving
                ? <CircularProgress size={16} color="inherit" />
                : <PrintIcon />}
            >
              {saving ? 'در حال صدور...' : confirmLabel}
            </Button>
            <Button variant="outlined" onClick={onClose} disabled={saving}>
              انصراف
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default BillingProfileDialog
